import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'

import useTableSort from '@/hooks/table/use-table-sort.hook'
import { uiGlobalSelector } from '@/state/ui/global/slices/index.slices'
import { SportsROLibraryColumnProps } from '@/state/sports-ro-calculator/models'

import { formatRowDate, getDateTimeFromIsoUTC } from '@/utils/date'
import { TimeZoneUTCType } from '@/enums'
import { TableHeadCellPropsType } from '@/state/ui/global/models'

import { ContributionsFreePlays } from '@/state/transactions/models'
import { dateTimeTableFormat } from '@/constants/date'
import { ApplicationState } from '@/state/root.models'

export const useSportsROCalculator = () => {
  const { t } = useTranslation()

  const { sportsRO, isLoading, pagination, filters } = useSelector(
    ({ sportsRO }: ApplicationState) => sportsRO,
  )

  const { isLoadingFull } = useSelector(uiGlobalSelector)

  let headCells: TableHeadCellPropsType<SportsROLibraryColumnProps>[] = [
    {
      id: 'dateAtText',
      label: t('transaction.placedDate'),
      width: 120,
      sort: true,
    },
    { id: 'betId', label: t('transaction.betId'), sort: true },
    { id: 'status', label: t('transaction.sportWagerStatus'), sort: true },
    { id: 'betType', label: t('transaction.sportWagerType'), sort: true },
    { id: 'riskAmount', label: t('transaction.riskAmount'), sort: true },
    { id: 'winAmount', label: t('transaction.winAmount'), sort: true },
    {
      id: 'wasRolloverAffected',
      label: t('transaction.wasRolloverAffected'),
      sort: true,
    },
    { id: 'rollOverAmount', label: t('labels.rolloverAmount'), sort: true },
  ]

  const formattedSportsROCalculator =
    sportsRO?.transactions?.map((sportRO) => {
      return {
        ...sportRO,
        placedDate: sportRO.placedDate
          ? formatRowDate(sportRO.placedDate)
          : '-',
        dateAtText: sportRO.placedDate
          ? new Date(sportRO.placedDate).getTime()
          : '-',
      }
    }) || []

  const sortedInitialState = 'id'

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    SportsROLibraryColumnProps,
    keyof SportsROLibraryColumnProps
  >(formattedSportsROCalculator, sortedInitialState, 'desc')

  const getXlsFormattedRow = (
    sportRO: ContributionsFreePlays,
  ): SportsROLibraryColumnProps => {
    return {
      ...sportRO,
      placedDate: sportRO.placedDate ? formatRowDate(sportRO.placedDate) : '-',
      dateAtText: sportRO.placedDate
        ? `${format(
            getDateTimeFromIsoUTC(
              sportRO.placedDate,
              TimeZoneUTCType['GMT-07 Pacific Time'],
            ),
            dateTimeTableFormat,
          )} PT`
        : '-',
      status: t(`transaction.sportWagerStatusOptions.${sportRO.status}`),
      betType: t(`transaction.sportWagerTypeOptions.${sportRO.betType}`),
      wasRolloverAffected: sportRO.wasRolloverAffected
        ? t('labels.true')
        : t('labels.false'),
    }
  }

  return {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    isLoading,
    isLoadingFull,
    headCells,
    filters,
    pagination,
    getXlsFormattedRow,
    sportsRO,
  }
}
